import React, { useEffect, useRef, useState } from "react";
import { Inertia } from "@inertiajs/inertia";
import AppLayout from "@/Layouts/AppLayout";
import OrganizationLayout from "@/Layouts/OrganizationLayout";

import { Tab, Tabs } from "@/Components/Tabs";
import { CheckSquare, MoreVertical, Star, User, X } from "react-feather";
import { InertiaLink } from "@inertiajs/inertia-react";
import { ActionMenu } from "@/Components/ActionMenu";
import { LoadingProvider, useLoading } from "@/Contexts/LoadingContext";
import { toast } from "react-toastify";
import { Dropdown, Subheader } from "@/Components";
import queryString from "query-string";
import { convertWeightToHumanReadable } from "@/Utilities/Utilities";
import Placeholder from "../../Components/Placeholder";
import { Teams as TeamsIcon } from "@/Icons";
import LayoutIcon from "@/Components/LayoutIcon";

export default function Teams({ teams = {} }) {
  const currentSort =
    queryString.parse(window.location.search).sort || "name.asc";

  const [deletingTeamId, setDeletingTeamId] = useState(false);
  const [duplicatingTeamId, setDuplicatingTeamId] = useState(false);
  const [sort, setSort] = useState(currentSort);

  const handleSort = (value) => {
    setSort(value);
    const newSearch = queryString.stringify({
      ...queryString.parse(window.location.search),
      sort: value,
    });
    Inertia.visit(`${window.location.pathname}?${newSearch}`);
  };

  let tabs = ["Active", "Archived"];

  let activeTeams = teams.data.filter((team) => !team.is_deleted);
  let archivedTeams = teams.data.filter((team) => team.is_deleted);

  const sortOptions = [
    { value: "name.asc", label: "Name A-Ö" },
    { value: "name.desc", label: "Name Ö-A" },
  ];

  const handleDelete = () => {
    const id = toast.loading("Please wait...");
    Inertia.delete(`/teams/${deletingTeamId}/force`, {
      onSuccess: () => {
        toast.update(id, {
          render: `Your team was successfully deleted`,
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: (error) => {
        toast.update(id, {
          render: `Something went wrong when deleting your team`,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onFinish: () => {
        setDeletingTeamId(null);
      },
    });
  };

  const handleDuplicate = () => {
    const id = toast.loading("Please wait...");
    Inertia.post(
      route("team.duplicate", duplicatingTeamId),
      {},
      {
        onSuccess: () => {
          toast.update(id, {
            render: "Your team was successfully duplicated",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
        },
        onError: () => {
          toast.update(id, {
            render: "Something went wrong when duplicating your team",
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
        },

        onFinish: () => {
          setDuplicatingTeamId(null);
        },
      },
    );
  };

  const [teamsEmission, setTeamsEmission] = useState();
  const { setIsLoaded } = useLoading();

  useEffect(() => {
    if (!teams.data.length) {
      return;
    }

    setIsLoaded(false);

    const url = new URL(route("teams.emissions.data"));
    teams.data.forEach((t) => url.searchParams.append("teams[]", t.id));

    // Fetch data
    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        setTeamsEmission(data);
        setIsLoaded(true);
      });
  }, []);

  const TeamGrid = ({ teams, isFavorite }) => {
    const sortedTeams = teams.sort((a, b) => {
      const [sortKey, sortDirection] = sort.split(".");
      if (a[sortKey] < b[sortKey]) {
        return sortDirection === "asc" ? -1 : 1;
      }
      if (a[sortKey] > b[sortKey]) {
        return sortDirection === "asc" ? 1 : -1;
      }
      return 0;
    });

    return (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4">
        {sortedTeams
          .filter((t) => t.is_favorite === isFavorite)
          .map((team) => (
            <TeamComponent
              key={team.id}
              team={team}
              onDelete={(teamId) => setDeletingTeamId(teamId)}
              onDuplicate={(teamId) => setDuplicatingTeamId(teamId)}
              emission={teamsEmission?.[team.id]}
            />
          ))}
      </div>
    );
  };

  return (
    <div className="mb-8">
      <Tabs items={tabs}>
        <Tab>
          <TeamGrid teams={activeTeams} isFavorite={true} />
          <div className="mb-2 flex items-end justify-between gap-4">
            <Subheader margin="" className="mt-12">
              All teams
            </Subheader>
            <Dropdown
              options={sortOptions}
              title="Sort on"
              placeholder={
                sortOptions.find((o) => o.value === sort)?.label || "Name A-Ö"
              }
              onSelect={(value) => {
                handleSort(value);
              }}
            />
          </div>
          <TeamGrid teams={activeTeams} isFavorite={false} />
        </Tab>

        <Tab>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3 3xl:grid-cols-4">
            {archivedTeams.map((team) => (
              <TeamComponent
                key={team.id}
                team={team}
                onDelete={(teamId) => setDeletingTeamId(teamId)}
                emission={teamsEmission?.[team.id]}
              />
            ))}
          </div>
        </Tab>
      </Tabs>
      {!!deletingTeamId && (
        <div className="fixed left-0 top-0 z-20 flex h-full w-full flex-col justify-center bg-black bg-opacity-50">
          <div
            className="absolute left-0 top-0 h-full w-full"
            onClick={() => setDeletingTeamId(null)}
          />
          <div className="relative mx-auto w-full max-w-2xl bg-white p-8 align-middle md:p-16">
            <X
              className="absolute right-0 top-0 h-12 w-12 cursor-pointer p-2"
              onClick={() => setDeletingTeamId(null)}
            />
            <h3 className="mb-3 text-xl font-semibold">
              Are you sure that you want to continue?
            </h3>
            <p>
              Deleting this question will permanently remove it from your team
              and you will not be able to restore it.
            </p>

            <div className="mt-5 flex w-full items-center justify-end border-t pt-8">
              <a
                className="cursor-pointer py-2 text-blue underline"
                onClick={() => setDeletingTeamId(null)}
              >
                Cancel
              </a>

              <button className="btn ml-3 self-end" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {!!duplicatingTeamId && (
        <div className="fixed left-0 top-0 z-20 flex h-full w-full flex-col justify-center bg-black bg-opacity-50">
          <div
            className="absolute left-0 top-0 h-full w-full"
            onClick={() => setDuplicatingTeamId(null)}
          />
          <div className="relative mx-auto w-full max-w-2xl bg-white p-8 align-middle md:p-16">
            <X
              className="absolute right-0 top-0 h-12 w-12 cursor-pointer p-2"
              onClick={() => setDuplicatingTeamId(null)}
            />
            <h3 className="mb-3 text-xl font-semibold">
              Duplicating team{" "}
              {teams.data.find((t) => t.id === duplicatingTeamId).name}
            </h3>
            <p>
              You are about to duplicate this team. This will create a new team
              with the same settings and activities as the original team.
              Reporters will not be carried over to the new team.
            </p>

            <div className="mt-5 flex w-full justify-end border-t pt-8">
              <a
                className="cursor-pointer py-4 text-blue underline"
                onClick={() => setDuplicatingTeamId(null)}
              >
                Cancel
              </a>

              <button className="btn ml-3 self-end" onClick={handleDuplicate}>
                Duplicate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const TeamComponent = ({
  team,
  emission,
  onDelete = (_) => null,
  onDuplicate = (_) => null,
}) => {
  const { isLoaded } = useLoading();

  const handleDelete = (forced = false) => {
    const id = toast.loading("Please wait...");
    Inertia.delete(`/teams/${team.id}${forced ? "/force" : ""}`, {
      onSuccess: () => {
        toast.update(id, {
          render: `Your team was successfully ${
            forced ? "deleted" : "archived"
          }`,
          type: "success",
          isLoading: false,
          autoClose: 5000,
        });
      },
      onError: () => {
        toast.update(id, {
          render: `Something went wrong when ${
            forced ? "deleting" : "archiving"
          } your team`,
          type: "error",
          isLoading: false,
          autoClose: 5000,
        });
      },
    });
  };

  const handleRestore = () => {
    const id = toast.loading("Please wait...");
    Inertia.post(
      `/teams/${team.id}/restore`,
      {},
      {
        onSuccess: () => {
          toast.update(id, {
            render: "Your team was successfully restored",
            type: "success",
            isLoading: false,
            autoClose: 5000,
          });
        },
        onError: () => {
          toast.update(id, {
            render: "Something went wrong when restoring your team",
            type: "error",
            isLoading: false,
            autoClose: 5000,
          });
        },
      },
    );
  };

  const handleFavorite = (event) => {
    event.preventDefault();
    Inertia.put(`/teams/${team.id}/favorite`);
  };

  let actionMenuItems;

  if (!team.is_deleted) {
    actionMenuItems = [
      {
        label: "Report on team",
        callback: () => Inertia.visit(route("team.overview", team)),
      },
      {
        label: "Edit team",
        callback: () => Inertia.visit(route("team.edit", team)),
      },
      {
        label: "Archive team",
        callback: () => handleDelete(),
      },
      {
        label: "Delete team",
        callback: () => onDelete(team.id),
      },
      {
        label: "Duplicate team",
        callback: () => onDuplicate(team.id),
      },
    ];
  } else {
    actionMenuItems = [
      {
        label: "Restore team",
        callback: () => handleRestore(),
      },
      {
        label: "Delete team",
        callback: () => onDelete(team.id),
      },
    ];
  }

  const actionMenuRef = useRef(null);
  const currentYear = new Date().getFullYear();

  return (
    <InertiaLink
      href={`/teams/${team.id}`}
      className="shadow-small flex cursor-pointer flex-col items-start gap-8 bg-gray-light px-8 py-6"
    >
      <div className="flex min-h-[100px] items-start self-stretch">
        <div className="flex grow flex-col items-start gap-1">
          <div className="self-stretch hyphens-auto break-all font-overpass text-2xl font-bold">
            {team.name}
          </div>

          {isLoaded ? (
            <div className="rounded-md bg-blue-lighter px-3 py-2 text-sm leading-none">
              {convertWeightToHumanReadable(emission?.total || 0)?.concat} CO₂e
              ({currentYear})
            </div>
          ) : (
            <Placeholder width="125px" height="30px" />
          )}
        </div>
        <div className="hover-circle relative z-10 ml-2">
          <MoreVertical
            size={24}
            className="shrink-0 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              actionMenuRef.current.toggleMenu();
            }}
          />
          <ActionMenu ref={actionMenuRef} items={actionMenuItems} />
        </div>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-2 self-stretch">
        <div className="flex shrink-0 items-start gap-4">
          <div className="flex items-end gap-2">
            <CheckSquare size={24} />
            <div>{team.questions.length} activites</div>
          </div>
          <div className="flex items-center gap-2">
            <User size={24} />
            <div>{team.users.length} Reporters</div>
          </div>
        </div>
        {!team.is_deleted && (
          <div className="flex grow justify-end">
            <div className="hover-circle relative">
              <Star
                size={24}
                fill={team.is_favorite ? "#FFB800" : "transparent"}
                stroke={team.is_favorite ? "#FFB800" : "#959BAB"}
                onClick={handleFavorite}
              />
            </div>
          </div>
        )}
      </div>
    </InertiaLink>
  );
};

Teams.layout = (page) => (
  <AppLayout title="Teams">
    <LoadingProvider>
      <OrganizationLayout
        header="Teams"
        byline="Go to your team to report and see status of activities"
        action={{
          label: "New team",
          callback: () => Inertia.visit(route("team.create")),
        }}
        icon={
          <LayoutIcon>
            <TeamsIcon size={40} className="text-black" />
          </LayoutIcon>
        }
      >
        {page}
      </OrganizationLayout>
    </LoadingProvider>
  </AppLayout>
);
